import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { supabase } from '../supabaseClient';
import { getBrowserTimeZone } from '../lib/browserTimeZone';

const compareTopicsByDiscoveryOrder = (a, b) => {
  const orderA = a?.order_index ?? 0;
  const orderB = b?.order_index ?? 0;
  if (orderA !== orderB) return orderA - orderB;

  const joinedA = a?.basket_joined_at ? new Date(a.basket_joined_at).getTime() : 0;
  const joinedB = b?.basket_joined_at ? new Date(b.basket_joined_at).getTime() : 0;
  return joinedA - joinedB;
};

const sortTopicsByDiscoveryOrder = (topics) =>
  [...(topics || [])].sort(compareTopicsByDiscoveryOrder);

/** Fire-and-forget stamp when a workspace's baskets/topics are loaded (open / switch). */
const stampWorkspaceLastOpened = (workspaceId) => {
  if (!workspaceId) return;

  void supabase
    .from('workspaces')
    .update({ last_opened_at: new Date().toISOString() })
    .eq('id', workspaceId)
    .then(({ error }) => {
      if (error) {
        console.warn('Failed to stamp last_opened_at:', error.message);
      }
    });
};

// Async thunk to fetch workspace details
const fetchWorkspaceDetails = createAsyncThunk(
  'workspaces/fetchWorkspaceDetails',
  async (workspaceId, { getState, rejectWithValue }) => {
    const state = getState().workspaces;
    // Return cached data if already loaded for this workspace
    if (state.isWorkspaceLoaded && state.currentWorkspace?.id === workspaceId) {
      return state.currentWorkspace;
    }

    try {
      stampWorkspaceLastOpened(workspaceId);

      // Fetch data only if not already loaded
      const { data: baskets, error: basketsError } = await supabase
        .from('baskets')
        .select('id, name, description, updated_at, is_pinned, is_locked')
        .eq('workspace_id', workspaceId)
        .order('updated_at', { ascending: false });

      if (basketsError) throw basketsError;

      // Then, fetch all topics with their related data
      const { data: topics, error: topicsError } = await supabase
        .from('topics')
        .select(`
          id,
          name,
          basket_id,
          tag_id,
          created_at,
          updated_at,
          order_index,
          basket_joined_at,
          workspace_id,
          user_id,
          relation_description,
          is_master,
          recall_count,
          not_recalled_count,
          total_recalls,
          last_recalled_at,
          is_ripe,
          is_anchor,
          is_locked,
          basket:basket_id (
            id,
            name,
            description,
            workspace_id,
            created_at,
            updated_at,
            order_index,
            is_locked
          ),
          concepts (
            id,
            text,
            is_highlighted,
            order_index,
            created_at,
            updated_at
          ),
          tags:tag_id (
            id,
            name,
            color,
            workspace_id,
            created_at
          )
        `)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (topicsError) throw topicsError;

      // Process the topics data
      const processedTopics = topics.map(topic => ({
        ...topic,
        tag: topic.tags,
        concepts: topic.concepts.map(concept => ({
          ...concept,
          is_highlighted: concept.is_highlighted
        }))
      }));


      // Group topics by basket, then sort each basket by discovery order
      const basketsMap = new Map(baskets.map(basket => [basket.id, { ...basket, topics: [] }]));
      processedTopics.forEach(topic => {
        if (topic.basket_id && basketsMap.has(topic.basket_id)) {
          basketsMap.get(topic.basket_id).topics.push(topic);
        }
      });

      const formattedBaskets = Array.from(basketsMap.values()).map(basket => ({
        ...basket,
        topics: sortTopicsByDiscoveryOrder(basket.topics)
      }));

   
      // Fetch tags
      const { data: tags, error: tagsError } = await supabase
        .from('tags')
        .select('id, name, color')
        .eq('workspace_id', workspaceId);

      if (tagsError) throw tagsError;

      return {
        id: workspaceId,
        baskets: formattedBaskets,
        topics: processedTopics,
        tags: tags,
        lastFetchedAt: Date.now(),
        isFetchedFromDatabase: true
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to delete a topic
const deleteTopic = createAsyncThunk(
  'workspaces/deleteTopic',
  async ({ workspaceId, topicId }, { rejectWithValue }) => {
    try {
      const { error } = await supabase
        .from('topics')
        .delete()
        .eq('id', topicId)
        .eq('workspace_id', workspaceId);

      if (error) throw error;
      return topicId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to delete a basket
const deleteBasket = createAsyncThunk(
  'workspaces/deleteBasket',
  async ({ workspaceId, basketId }, { rejectWithValue }) => {
    try {
      // Keep topics: clear basket_id first so deleting the basket does not remove or orphan rows per DB FK rules
      const { error: detachError } = await supabase
        .from('topics')
        .update({ basket_id: null })
        .eq('basket_id', basketId)
        .eq('workspace_id', workspaceId);

      if (detachError) throw detachError;

      const { error: deleteError } = await supabase
        .from('baskets')
        .delete()
        .eq('id', basketId)
        .eq('workspace_id', workspaceId);

      if (deleteError) throw deleteError;
      return basketId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to add a new topic
const addTopic = createAsyncThunk(
  'workspaces/addTopic',
  async (newTopicData, { rejectWithValue, getState }) => {
    console.log('Starting addTopic with:', newTopicData);
    try {
      const { workspaceId, newTopic } = newTopicData;
      let tagId = newTopic.tag_id; // Use existing tag_id if provided

      // Only handle tag creation if it's a new tag
      if (newTopic.tag && !tagId) {
        console.log('Processing new tag:', newTopic.tag);
        // First check if the tag already exists in the state
        const state = getState();
        const existingTag = state.workspaces.currentWorkspace.tags.find(
          tag => tag.name.toLowerCase() === newTopic.tag.name.toLowerCase() && 
                tag.workspace_id === workspaceId
        );

        if (existingTag) {
          console.log('Using existing tag from state:', existingTag);
          tagId = existingTag.id;
        } else {
          // Create new tag
          console.log('Creating new tag...');
          const newTag = {
            name: newTopic.tag.name,
            color: newTopic.tag.color,
            created_by: newTopic.user_id,
            workspace_id: workspaceId
          };
          console.log('New tag data:', newTag);

          const { data: createdTag, error: createError } = await supabase
            .from('tags')
            .insert([newTag])
            .select()
            .single();

          if (createError) {
            console.error('Error creating tag:', createError);
            throw createError;
          }
          console.log('Successfully created tag:', createdTag);
          tagId = createdTag.id;
        }
      }

      let insertedTopic;

      if (newTopic.basket_id) {
        // Atomic create + append order_index for create-into-basket
        console.log('Creating topic in basket via RPC:', {
          name: newTopic.name,
          basket_id: newTopic.basket_id,
          workspace_id: workspaceId,
          tag_id: tagId
        });

        const { data: createdTopic, error: topicError } = await supabase.rpc(
          'create_topic_in_basket',
          {
            p_name: newTopic.name,
            p_basket_id: newTopic.basket_id,
            p_workspace_id: workspaceId,
            p_user_id: newTopic.user_id,
            p_tag_id: tagId || null
          }
        );

        if (topicError) {
          console.error('Error creating topic in basket:', topicError);
          throw topicError;
        }
        insertedTopic = createdTopic;
        void supabase.auth
          .getSession()
          .then(({ data: { session } }) => {
            if (!session?.access_token) return;

            return fetch('/api/brevo-contact', {
              method: 'POST',
              keepalive: true,
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ action: 'sync', timezone: getBrowserTimeZone() }),
            }).then((response) => {
              if (!response.ok) {
                console.warn('Brevo placement sync failed:', response.status);
              }
            });
          })
          .catch((error) => {
            console.warn('Brevo placement sync error:', error);
          });
      } else {
        // Unplaced create — BSI places later via place_topic_in_basket
        const topicToInsert = {
          name: newTopic.name,
          workspace_id: workspaceId,
          basket_id: null,
          tag_id: tagId,
          user_id: newTopic.user_id,
          order_index: newTopic.order_index || 0
        };

        console.log('Inserting unplaced topic:', topicToInsert);

        const { data, error: topicError } = await supabase
          .from('topics')
          .insert([topicToInsert])
          .select()
          .single();

        if (topicError) {
          console.error('Error inserting topic:', topicError);
          throw topicError;
        }
        insertedTopic = data;
      }
      console.log('Successfully inserted topic:', insertedTopic);

      // Update the basket's updated_at timestamp
      if (newTopic.basket_id) {
        const { error: basketError } = await supabase
          .from('baskets')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', newTopic.basket_id)
          .eq('workspace_id', workspaceId);

        if (basketError) throw basketError;
      }

      // Insert concepts if any
      if (newTopic.concepts && newTopic.concepts.length > 0) {
        console.log('Processing concepts:', newTopic.concepts);
        const conceptsToInsert = newTopic.concepts.map(concept => ({
          text: concept.text || concept.name,
          topic_id: insertedTopic.id,
          is_highlighted: concept.is_highlighted
        }));
        console.log('Concepts to insert:', conceptsToInsert);

        const { data: insertedConcepts, error: conceptsError } = await supabase
          .from('concepts')
          .insert(conceptsToInsert);

        if (conceptsError) {
          console.error('Error inserting concepts:', conceptsError);
          throw conceptsError;
        }
        console.log('Successfully inserted concepts:', insertedConcepts);
      }

      // Fetch the complete topic data including related data
      console.log('Fetching complete topic data for ID:', insertedTopic.id);
      const { data: completeTopic, error: fetchError } = await supabase
        .from('topics')
        .select(`
          *,
          concepts(*),
          tags(*)
        `)
        .eq('id', insertedTopic.id)
        .single();

      if (fetchError) {
        console.error('Error fetching complete topic:', fetchError);
        throw fetchError;
      }
      console.log('Successfully fetched complete topic:', completeTopic);

      const processedTopic = {
        ...completeTopic,
        tag: completeTopic.tags,
        concepts: completeTopic.concepts.map(concept => ({
          ...concept,
          is_highlighted: !!concept.is_highlighted
        }))
      };
      console.log('Returning processed topic:', processedTopic);
      return processedTopic;

    } catch (error) {
      console.error('Error in addTopic:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to change a topic's basket
const changeTopic = createAsyncThunk(
  'workspaces/changeTopic',
  async ({ topicId, newBasketId, workspaceId }, { getState, rejectWithValue }) => {
    try {
      if (!newBasketId) {
        throw new Error('Target basket ID is required');
      }

      const { data: nextIndex, error } = await supabase.rpc('place_topic_in_basket', {
        p_topic_id: topicId,
        p_basket_id: newBasketId,
        p_set_relation_description: false
      });

      if (error) throw error;

      void supabase.auth
        .getSession()
        .then(({ data: { session } }) => {
          if (!session?.access_token) return;

          return fetch('/api/brevo-contact', {
            method: 'POST',
            keepalive: true,
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'sync', timezone: getBrowserTimeZone() }),
          }).then((response) => {
            if (!response.ok) {
              console.warn('Brevo placement sync failed:', response.status);
            }
          });
        })
        .catch((syncError) => {
          console.warn('Brevo placement sync error:', syncError);
        });

      const { error: basketError } = await supabase
        .from('baskets')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', newBasketId)
        .eq('workspace_id', workspaceId);

      if (basketError) throw basketError;

      const workspace = getState().workspaces?.currentWorkspace;
      const existingTopic =
        workspace?.topics?.find((t) => t.id === topicId) ||
        workspace?.baskets?.flatMap((b) => b.topics || [])?.find((t) => t.id === topicId) ||
        null;

      return {
        ...(existingTopic || { id: topicId }),
        basket_id: newBasketId,
        order_index: nextIndex,
        basket_joined_at: new Date().toISOString()
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to remove a topic from a basket
const removeTopicFromBasket = createAsyncThunk(
  'workspaces/removeTopicFromBasket',
  async ({ topicId, basketId, workspaceId }, { getState, rejectWithValue }) => {
    try {
      // Just update basket_id, no need to fetch all data
      const { error } = await supabase
        .from('topics')
        .update({ basket_id: null })
        .eq('id', topicId)
        .eq('workspace_id', workspaceId);

      if (error) throw error;

      // Get the existing topic data from state
      const state = getState();
      const existingTopic = state.workspaces.currentWorkspace.topics.find(t => t.id === topicId);
      
      // Return the existing topic data with updated basket_id
      // Make sure to preserve both concepts and tags
      const updatedTopic = {
        ...existingTopic,
        basket_id: null,
        concepts: existingTopic.concepts || [],
        tags: existingTopic.tags || existingTopic.tag // Preserve tags data
      };

      return { topicId, basketId, updatedTopic };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch a basket by its ID
const fetchBasketById = createAsyncThunk(
  'workspaces/fetchBasketById',
  async (basketId, { getState }) => {
    const state = getState();
    const basket = state.workspaces.currentWorkspace.baskets.find(b => b.id === basketId);
    if (!basket) {
      throw new Error('Basket not found');
    }
    return basket;
  }
);

// Async thunk to rename a basket
const renameBasket = createAsyncThunk(
  'workspaces/renameBasket',
  async ({ basketId, newName, workspaceId }, { rejectWithValue }) => {
    try {
      console.log('Renaming basket:', { basketId, newName, workspaceId });
      
      // First, check if the basket exists
      const { data: existingBaskets, error: checkError } = await supabase
        .from('baskets')
        .select('*')
        .eq('id', basketId)
        .eq('workspace_id', workspaceId);

      if (checkError) {
        console.error('Error checking basket:', checkError);
        throw new Error(`Error checking basket: ${checkError.message}`);
      }

      if (!existingBaskets || existingBaskets.length === 0) {
        console.error('No basket found:', { basketId, workspaceId });
        throw new Error(`No basket found with ID ${basketId} in workspace ${workspaceId}`);
      }

      // If the basket exists, proceed with the update
      const { data, error } = await supabase
        .from('baskets')
        .update({ name: newName })
        .eq('id', basketId)
        .eq('workspace_id', workspaceId)
        .select();

      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error('No rows were updated');
      }
      
      console.log('Basket renamed successfully:', data[0]);
      return data[0];
    } catch (error) {
      console.error('Error in renameBasket:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update a basket
const updateBasket = createAsyncThunk(
  'workspaces/updateBasket',
  async ({ basketId, updatedData, workspaceId }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('baskets')
        .update({
          ...updatedData,
          updated_at: new Date().toISOString()
        })
        .eq('id', basketId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update the createBasket thunk
export const createBasket = createAsyncThunk(
  'workspaces/addBasket',
  async (basket, { rejectWithValue }) => {
    try {
      console.log('createBasket thunk - Starting with data:', basket);

      if (!basket.workspace_id) {
        console.error('Missing workspace_id in basket data:', basket);
        throw new Error('workspace_id is required');
      }

      const { data, error } = await supabase
        .from('baskets')
        .insert([{
          name: basket.name,
          workspace_id: basket.workspace_id,
          description: basket.description || '',
          order_index: basket.order_index || 0
        }])
        .select('*')
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('createBasket thunk - Successfully created basket:', data);

      // Return the created basket with an empty topics array
      return {
        ...data,
        topics: []
      };
    } catch (error) {
      console.error('Error in createBasket:', error);
      return rejectWithValue(error.message || 'Failed to create basket');
    }
  }
);

// Update the moveTopic thunk
const moveTopic = createAsyncThunk(
  'workspaces/moveTopic',
  async ({ topicId, fromBasketId, toBasketId, workspaceId, relationDescription }, { rejectWithValue, getState }) => {
    try {
      if (!toBasketId) {
        throw new Error('Target basket ID is required');
      }
      
      const state = getState();
      const workspace = state.workspaces?.currentWorkspace;
      const existingTopicFromTopicsArray = workspace?.topics?.find((t) => t.id === topicId);
      const existingTopicFromFromBasket = fromBasketId
        ? workspace?.baskets
            ?.find((b) => b.id === fromBasketId)
            ?.topics?.find((t) => t.id === topicId)
        : null;
      const existingTopicFromAnyBasket = workspace?.baskets
        ?.flatMap((b) => b.topics || [])
        ?.find((t) => t.id === topicId);

      const existingTopic =
        existingTopicFromTopicsArray ||
        existingTopicFromFromBasket ||
        existingTopicFromAnyBasket ||
        null;

      const { data: nextIndex, error } = await supabase.rpc('place_topic_in_basket', {
        p_topic_id: topicId,
        p_basket_id: toBasketId,
        p_relation_description: relationDescription || null,
        p_set_relation_description: true
      });

      if (error) throw error;

      const basketJoinedAt = new Date().toISOString();

      // Simplified topic processing
      const processedTopic = {
        ...(existingTopic || {}),
        id: topicId,
        basket_id: toBasketId,
        relation_description: relationDescription || null,
        order_index: nextIndex,
        basket_joined_at: basketJoinedAt,
        concepts: existingTopic?.concepts || [],
        tag: existingTopic?.tag || existingTopic?.tags || null,
        tags: existingTopic?.tags || existingTopic?.tag || null
      };

      // Update timestamps
      if (fromBasketId) {
        await supabase
          .from('baskets')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', fromBasketId)
          .eq('workspace_id', workspaceId);
      }

      if (toBasketId) {
        await supabase
          .from('baskets')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', toBasketId)
          .eq('workspace_id', workspaceId);
      }

      return { topic: processedTopic };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Creates a basket and places a topic inside it via a single transactional
// Supabase RPC, so a partial failure can never leave an empty orphan basket.
export const createBasketAndPlaceTopic = createAsyncThunk(
  'workspaces/createBasketAndPlaceTopic',
  async ({ workspaceId, basketName, topicId }, { getState, rejectWithValue }) => {
    try {
      if (!workspaceId || !basketName?.trim() || !topicId) {
        throw new Error('Workspace, basket name and topic are required');
      }

      const { data: basketRow, error } = await supabase.rpc('create_basket_and_place_topic', {
        p_workspace_id: workspaceId,
        p_basket_name: basketName.trim(),
        p_topic_id: topicId,
      });

      if (error) throw error;
      if (!basketRow?.id) {
        throw new Error('Failed to create basket: missing basket ID');
      }

      // The topic already lives in Redux state with its tags/concepts joins,
      // so build the placed topic from state instead of re-fetching.
      const workspace = getState().workspaces?.currentWorkspace;
      const existingTopic =
        workspace?.topics?.find((t) => t.id === topicId) ||
        workspace?.baskets?.flatMap((b) => b.topics || [])?.find((t) => t.id === topicId) ||
        null;

      const placedTopic = {
        ...(existingTopic || { id: topicId }),
        basket_id: basketRow.id,
        order_index: 0,
        basket_joined_at: new Date().toISOString(),
        is_anchor: true,
      };

      return {
        basket: { ...basketRow, topics: [placedTopic] },
        topicId,
        fromBasketId: existingTopic?.basket_id ?? null,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create basket and place topic');
    }
  }
);

// Find the updateTopic action creator
export const updateTopic = createAsyncThunk(
  'workspaces/updateTopic',
  async ({ topicId, updates, workspaceId }, { rejectWithValue }) => {
    try {
      // First update the topic
      const { data: updatedTopic, error: updateError } = await supabase
        .from('topics')
        .update(updates)
        .eq('id', topicId)
        .eq('workspace_id', workspaceId)
        .select(`
          *,
          concepts (*),
          tags:tag_id (*)
        `)
        .single();

      if (updateError) throw updateError;

      // Process the topic data to match the expected format
      const processedTopic = {
        ...updatedTopic,
        tag: updatedTopic.tags,
        concepts: updatedTopic.concepts || []
      };

      return { topic: processedTopic };
    } catch (error) {
      console.error('Error in updateTopic:', error);
      return rejectWithValue(error);
    }
  }
);

// Add this with the other async thunks
const updateConceptImportance = createAsyncThunk(
  'workspaces/updateConceptImportance',
  async ({ conceptId, topicId, newHighlightedState }, { rejectWithValue }) => {
    try {
      // First update all concepts to not highlighted
      const { error: updateAllError } = await supabase
        .from('concepts')
        .update({ is_highlighted: false })
        .eq('topic_id', topicId);

      if (updateAllError) throw updateAllError;

      // Then update the selected concept if it's being highlighted
      if (newHighlightedState) {
        const { error: updateSelectedError } = await supabase
          .from('concepts')
          .update({ is_highlighted: true })
          .eq('id', conceptId);

        if (updateSelectedError) throw updateSelectedError;
      }

      return { conceptId, topicId, newHighlightedState };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  currentWorkspace: {
    id: null,
    baskets: [],
    topics: [],
    tags: [], // Initialize tags as an empty array
    status: 'idle',
    error: null,
    lastFetchedAt: null,
    isFetchedFromDatabase: false,
  },
  currentBasket: null,
  basketContentChanged: false,
  isWorkspaceLoaded: false,
  /** Ephemeral return target after TopicDetail; not meant to survive hard refresh. */
  connectionsReturn: null,
};

// Create the workspaces slice
const workspacesSlice = createSlice({
  name: 'workspaces',
  initialState,
  reducers: {
    // Add a new basket to the current workspace
    addBasket: (state, action) => {
      const newBasket = {
        ...action.payload,
        topics: action.payload.topics || []
      };
      state.currentWorkspace.baskets.push(newBasket);
    },
    // Update the last fetched timestamp
    updateLastFetchedAt: (state, action) => {
      state.currentWorkspace.lastFetchedAt = action.payload;
    },
    // Change a topic's basket (this might be redundant with the changeTopic thunk)
    changeTopic: (state, action) => {
      const { topicId, newBasketId } = action.payload;
      const workspace = state.currentWorkspace;
      
      // Find the topic in the current workspace
      const topic = workspace.topics.find(t => t.id === topicId);
      if (topic) {
        // Remove the topic from its current basket
        const oldBasket = workspace.baskets.find(b => b.id === topic.basket_id);
        if (oldBasket) {
          oldBasket.topics = oldBasket.topics.filter(t => t.id !== topicId);
        }
        
        // Update the topic's basket_id
        topic.basket_id = newBasketId;
        
        // Add the topic to the new basket
        const newBasket = workspace.baskets.find(b => b.id === newBasketId);
        if (newBasket) {
          newBasket.topics.push(topic);
        }
      }
    },
    // Set the basket content changed flag
    setBasketContentChanged: (state, action) => {
      state.basketContentChanged = action.payload;
    },
    // Optimistically patch a topic's recall stats everywhere it lives in the
    // store. The topic is duplicated across currentBasket.topics, the flat
    // currentWorkspace.topics list, and the workspace baskets tree; Immer
    // produces an independent draft per path, so each copy must be patched so
    // both the basket card and the workspace-level mastery celebration stay
    // consistent without a refetch.
    applyTopicRecall: (state, action) => {
      const { topicId, recalled } = action.payload;
      const recalledAt = new Date().toISOString();

      const patch = (topic) => {
        if (!topic) return;
        if (recalled === true) {
          topic.recall_count = (topic.recall_count ?? 0) + 1;
          topic.total_recalls = (topic.total_recalls ?? 0) + 1;
          topic.last_recalled_at = recalledAt;
        } else {
          topic.recall_count = (topic.recall_count ?? 0) - 1;
          topic.not_recalled_count = (topic.not_recalled_count ?? 0) + 1;
        }
        topic.is_ripe = topic.recall_count >= 3;
      };

      if (state.currentBasket?.topics) {
        patch(state.currentBasket.topics.find((t) => t.id === topicId));
      }

      const workspace = state.currentWorkspace;
      if (workspace) {
        if (workspace.topics) {
          patch(workspace.topics.find((t) => t.id === topicId));
        }
        if (workspace.baskets) {
          workspace.baskets.forEach((b) => {
            if (b.topics) patch(b.topics.find((t) => t.id === topicId));
          });
        }
      }
    },
    // Sync TopicDetail concept edits into the workspace cache (no refetch).
    topicConceptsSynced: (state, action) => {
      const { topicId, concepts } = action.payload;
      if (!topicId) return;
      const nextConcepts = concepts || [];

      const patch = (topic) => {
        if (!topic) return;
        topic.concepts = nextConcepts;
      };

      if (state.currentBasket?.topics) {
        patch(state.currentBasket.topics.find((t) => t.id === topicId));
      }

      const workspace = state.currentWorkspace;
      if (workspace) {
        if (workspace.topics) {
          patch(workspace.topics.find((t) => t.id === topicId));
        }
        if (workspace.baskets) {
          workspace.baskets.forEach((b) => {
            if (b.topics) patch(b.topics.find((t) => t.id === topicId));
          });
        }
      }
    },
    connectionsReturnSaved: (state, action) => {
      state.connectionsReturn = action.payload || null;
    },
    connectionsReturnCleared: (state) => {
      state.connectionsReturn = null;
    },
    setCurrentBasket: (state, action) => {
      const basketId = action.payload;
      console.log('Setting current basket with ID:', basketId);
      if (basketId) {
        const basket = state.currentWorkspace.baskets.find(b => b.id === basketId);
        if (basket) {
          console.log('Current basket set to:', basket.name);
          state.currentBasket = basket;
        } else {
          console.warn(`Basket with id ${basketId} not found in current workspace`);
          state.currentBasket = null;
        }
      } else {
        console.log('Clearing current basket');
        state.currentBasket = null;
      }
    },
    // Rename updateBaskets to setBaskets
    setBaskets: (state, action) => {
      state.currentWorkspace.baskets = action.payload;
    },

    // Add a new reducer to update a single basket in the state
    updateBasketInState: (state, action) => {
      const updatedBasket = action.payload;
      if (!updatedBasket?.id || !state.currentWorkspace) return;

      const index = state.currentWorkspace.baskets.findIndex(b => b.id === updatedBasket.id);
      if (index !== -1) {
        const existing = state.currentWorkspace.baskets[index];
        state.currentWorkspace.baskets[index] = {
          ...existing,
          ...updatedBasket,
          topics: existing.topics,
        };
      }
      if (state.currentBasket && state.currentBasket.id === updatedBasket.id) {
        state.currentBasket = {
          ...state.currentBasket,
          ...updatedBasket,
          topics: state.currentBasket.topics,
        };
      }

      const patchNestedBasket = (topic) => {
        if (!topic?.basket || topic.basket.id !== updatedBasket.id) return;
        if (updatedBasket.name !== undefined) {
          topic.basket.name = updatedBasket.name;
        }
        if (updatedBasket.description !== undefined) {
          topic.basket.description = updatedBasket.description;
        }
        if (updatedBasket.updated_at !== undefined) {
          topic.basket.updated_at = updatedBasket.updated_at;
        }
      };

      (state.currentWorkspace.topics || []).forEach(patchNestedBasket);
      (state.currentWorkspace.baskets || []).forEach((basketRow) => {
        (basketRow.topics || []).forEach(patchNestedBasket);
      });
    },
    setWorkspaceLoaded: (state, action) => {
      state.isWorkspaceLoaded = action.payload;
    },
    resetWorkspaceState: (state) => {
      state.isWorkspaceLoaded = false;
      state.currentWorkspace = initialState.currentWorkspace;
      state.currentBasket = null;
      state.basketContentChanged = false;
      state.connectionsReturn = null;
    },
    addBasketToWorkspace: (state, action) => {
      const { basket } = action.payload;
      
      if (!basket.id) {
        console.error('addBasketToWorkspace - Basket is missing ID:', basket);
        return; // Early return to prevent errors
      }
      
      const existingBasketIndex = state.currentWorkspace.baskets.findIndex(b => b.id === basket.id);
      
      if (existingBasketIndex !== -1) {
        // Update existing basket
        state.currentWorkspace.baskets[existingBasketIndex] = {
          ...state.currentWorkspace.baskets[existingBasketIndex],
          ...basket,
          topics: [...(basket.topics || [])]
        };
      } else {
        // Add new basket
        state.currentWorkspace.baskets.push({
          ...basket,
          topics: basket.topics || []
        });
      }
    },
    moveBasketToTop: (state, action) => {
      const { basketId } = action.payload;
      
      if (!basketId) {
        return; // Early return to prevent errors
      }
      
      const { baskets } = state.currentWorkspace;
      
      // Find the basket to move
      const basketIndex = baskets.findIndex(b => b.id === basketId);
      
      if (basketIndex === -1) {
        return; // Early return if basket not found
      }
      
      if (basketIndex > 0) { // Only move if not already at top
        const [basket] = baskets.splice(basketIndex, 1);
        baskets.unshift(basket);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchWorkspaceDetails states
      .addCase(fetchWorkspaceDetails.pending, (state) => {
        state.currentWorkspace.status = 'loading';
      })
      .addCase(fetchWorkspaceDetails.fulfilled, (state, action) => {
        state.currentWorkspace = {
          ...action.payload,
          status: 'succeeded',
          error: null
        };
        state.basketContentChanged = false;
        state.isWorkspaceLoaded = true;
        
        // If there's a current basket, update it
        if (state.currentBasket) {
          const updatedBasket = action.payload.baskets.find(b => b.id === state.currentBasket.id);
          state.currentBasket = updatedBasket || null;
        }
      })
      .addCase(fetchWorkspaceDetails.rejected, (state, action) => {
        state.currentWorkspace.status = 'failed';
        state.currentWorkspace.error = action.error.message;
      })
      // Handle deleteTopic states
      .addCase(deleteTopic.fulfilled, (state, action) => {
        const deletedTopicId = action.payload;
        state.currentWorkspace.topics = state.currentWorkspace.topics.filter(topic => topic.id !== deletedTopicId);
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => ({
          ...basket,
          topics: (basket.topics || []).filter(topic => topic.id !== deletedTopicId)
        }));
      })
      .addCase(deleteTopic.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Handle deleteBasket states
      .addCase(deleteBasket.fulfilled, (state, action) => {
        const deletedBasketId = action.payload;
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.filter(
          basket => basket.id !== deletedBasketId
        );
        state.currentWorkspace.topics = state.currentWorkspace.topics.map(topic => {
          if (topic.basket_id !== deletedBasketId) {
            return topic;
          }
          const { basket: _basketJoin, ...rest } = topic;
          return {
            ...rest,
            basket_id: null,
            concepts: topic.concepts || [],
            tags: topic.tags || topic.tag,
          };
        });
        if (state.currentBasket && state.currentBasket.id === deletedBasketId) {
          state.currentBasket = null;
        }
      })
      .addCase(deleteBasket.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Handle addTopic states
      .addCase(addTopic.fulfilled, (state, action) => {
        const updatedTopic = action.payload;
        if (updatedTopic) {
          // Update topics array
          const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === updatedTopic.id);
          if (topicIndex !== -1) {
            state.currentWorkspace.topics[topicIndex] = updatedTopic;
          } else {
            state.currentWorkspace.topics.push(updatedTopic);
          }

          // Update basket's topics and updated_at (incl. first item into an empty basket)
          if (updatedTopic.basket_id) {
            const basketIndex = state.currentWorkspace.baskets.findIndex(b => b.id === updatedTopic.basket_id);
            if (basketIndex !== -1) {
              const basket = state.currentWorkspace.baskets[basketIndex];
              // Remove basket from current position
              state.currentWorkspace.baskets.splice(basketIndex, 1);
              // Update basket
              const existingTopics = basket.topics || [];
              basket.topics = existingTopics.filter(t => t.id !== updatedTopic.id);
              basket.topics.push(updatedTopic);
              basket.updated_at = new Date().toISOString();
              // Add basket to the beginning of the array
              state.currentWorkspace.baskets.unshift(basket);
            }
          }

          // Add the new tag to the tags array if it doesn't exist
          if (updatedTopic.tag && !state.currentWorkspace.tags.some(t => t.id === updatedTopic.tag.id)) {
            state.currentWorkspace.tags.push(updatedTopic.tag);
          }
        }
      })
      .addCase(addTopic.rejected, (state, action) => {
        console.error('addTopic.rejected:', action.error);
        state.currentWorkspace.error = action.error.message;
      })
      // Handle changeTopic states
      .addCase(changeTopic.fulfilled, (state, action) => {
        const updatedTopic = action.payload;
        
        // Update the topic in the topics array
        const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === updatedTopic.id);
        if (topicIndex !== -1) {
          state.currentWorkspace.topics[topicIndex] = updatedTopic;
        }

        // Update baskets
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => {
          // Remove topic from old basket
          if (basket.topics.some(t => t.id === updatedTopic.id)) {
            return {
              ...basket,
              topics: basket.topics.filter(t => t.id !== updatedTopic.id)
            };
          }
          // Add topic to new basket and update timestamp
          if (basket.id === updatedTopic.basket_id) {
            const updatedBasket = {
              ...basket,
              topics: [...basket.topics, updatedTopic],
              updated_at: new Date().toISOString()
            };
            // Move this basket to the beginning of the array
            const otherBaskets = state.currentWorkspace.baskets.filter(b => b.id !== basket.id);
            state.currentWorkspace.baskets = [updatedBasket, ...otherBaskets];
            return updatedBasket;
          }
          return basket;
        });

        // Update currentBasket if it exists
        if (state.currentBasket && state.currentBasket.id === updatedTopic.basket_id) {
          const existingTopicIndex = state.currentBasket.topics.findIndex(t => t.id === updatedTopic.id);
          if (existingTopicIndex === -1) {
            state.currentBasket.topics.push(updatedTopic);
          } else {
            state.currentBasket.topics[existingTopicIndex] = updatedTopic;
          }
        }
      })
      // Handle removeTopicFromBasket states
      .addCase(removeTopicFromBasket.fulfilled, (state, action) => {
        const { topicId, basketId, updatedTopic } = action.payload;
        
        // Update the topic in the main topics array
        const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === topicId);
        if (topicIndex !== -1) {
          state.currentWorkspace.topics[topicIndex] = updatedTopic;
        }

        // Remove the topic from the basket
        const basketIndex = state.currentWorkspace.baskets.findIndex(b => b.id === basketId);
        if (basketIndex !== -1) {
          state.currentWorkspace.baskets[basketIndex].topics = 
            state.currentWorkspace.baskets[basketIndex].topics.filter(t => t.id !== topicId);
        }

        // Update currentBasket if it's the affected basket
        if (state.currentBasket && state.currentBasket.id === basketId) {
          state.currentBasket.topics = state.currentBasket.topics.filter(t => t.id !== topicId);
        }
      })
      .addCase(removeTopicFromBasket.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Handle fetchBasketById states
      .addCase(fetchBasketById.fulfilled, (state, action) => {
        state.currentBasket = action.payload;
      })
      .addCase(fetchBasketById.rejected, (state, action) => {
        state.error = action.error.message;
      })
      // Handle renameBasket states
      .addCase(renameBasket.fulfilled, (state, action) => {
        const updatedBasket = action.payload;
        const basketIndex = state.currentWorkspace.baskets.findIndex(b => b.id === updatedBasket.id);
        if (basketIndex !== -1) {
          state.currentWorkspace.baskets[basketIndex].name = updatedBasket.name;
        }
        if (state.currentBasket && state.currentBasket.id === updatedBasket.id) {
          state.currentBasket.name = updatedBasket.name;
        }
      })
      .addCase(renameBasket.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Handle updateBasket states
      .addCase(updateBasket.fulfilled, (state, action) => {
        workspacesSlice.caseReducers.updateBasketInState(state, action);
      })
      .addCase(updateBasket.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Add these cases for moveTopic
      .addCase(moveTopic.fulfilled, (state, action) => {
        const { topic } = action.payload;
        
        // Update the topic in the main topics array
        const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === topic.id);
        if (topicIndex !== -1) {
          state.currentWorkspace.topics[topicIndex] = {
            ...topic,
            relation_description: topic.relation_description
          };
        }

        // Update baskets
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => {
          // Remove from old basket
          if (basket.topics.some(t => t.id === topic.id)) {
            return {
              ...basket,
              topics: basket.topics.filter(t => t.id !== topic.id),
              updated_at: new Date().toISOString()
            };
          }
          // Add to new basket
          if (basket.id === topic.basket_id) {
            return {
              ...basket,
              topics: [...basket.topics, {
                ...topic,
                relation_description: topic.relation_description
              }],
              updated_at: new Date().toISOString()
            };
          }
          return basket;
        });
      })
      .addCase(moveTopic.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Add these cases for createBasket
      .addCase(createBasket.fulfilled, (state, action) => {
        const newBasket = action.payload;
        
        // Check if basket already exists to prevent duplication
        const basketExists = state.currentWorkspace.baskets.some(
          basket => basket.id === newBasket.id
        );
        
        if (!basketExists) {
          // Only add if it doesn't exist
          state.currentWorkspace.baskets = [
            newBasket,
            ...state.currentWorkspace.baskets
          ];
        }
        
        state.status = 'succeeded';
      })
      .addCase(createBasket.rejected, (state, action) => {
        console.error('createBasket.rejected:', action.payload);
        state.currentWorkspace.error = action.payload;
      })
      // Handle createBasketAndPlaceTopic in one state update so the grid
      // never renders the new basket without its topic.
      .addCase(createBasketAndPlaceTopic.fulfilled, (state, action) => {
        const { basket, topicId } = action.payload;
        const placedTopic = basket.topics[0];

        // Update the flat topics array
        const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === topicId);
        if (topicIndex !== -1) {
          state.currentWorkspace.topics[topicIndex] = {
            ...state.currentWorkspace.topics[topicIndex],
            ...placedTopic
          };
        }

        // Remove the topic from its previous basket, then prepend the new
        // basket (already containing the topic) if it isn't there yet.
        const withoutTopic = state.currentWorkspace.baskets.map(b =>
          b.topics?.some(t => t.id === topicId)
            ? { ...b, topics: b.topics.filter(t => t.id !== topicId), updated_at: new Date().toISOString() }
            : b
        );

        const basketExists = withoutTopic.some(b => b.id === basket.id);
        state.currentWorkspace.baskets = basketExists
          ? withoutTopic.map(b => (b.id === basket.id ? basket : b))
          : [basket, ...withoutTopic];

        state.status = 'succeeded';
      })
      .addCase(createBasketAndPlaceTopic.rejected, (state, action) => {
        console.error('createBasketAndPlaceTopic.rejected:', action.payload);
        state.currentWorkspace.error = action.payload;
      })
      // Add these cases in the extraReducers builder
      .addCase(updateTopic.fulfilled, (state, action) => {
        const { topic: updatedTopic } = action.payload;
        
        // Update in topics array
        const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === updatedTopic.id);
        if (topicIndex !== -1) {
          state.currentWorkspace.topics[topicIndex] = {
            ...state.currentWorkspace.topics[topicIndex],
            ...updatedTopic
          };
        }

        // Update in baskets
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => ({
          ...basket,
          topics: basket.topics.map(topic => 
            topic.id === updatedTopic.id 
              ? { ...topic, ...updatedTopic }
              : topic
          )
        }));

        // Update in currentBasket if it exists
        if (state.currentBasket && state.currentBasket.topics) {
          state.currentBasket.topics = state.currentBasket.topics.map(topic =>
            topic.id === updatedTopic.id 
              ? { ...topic, ...updatedTopic }
              : topic
          );
        }
      })
      .addCase(updateTopic.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      .addCase(updateConceptImportance.fulfilled, (state, action) => {
        const { conceptId, topicId, newHighlightedState } = action.payload;
        
        // Update in topics array
        state.currentWorkspace.topics = state.currentWorkspace.topics.map(topic => {
          if (topic.id === topicId) {
            return {
              ...topic,
              concepts: topic.concepts.map(concept => ({
                ...concept,
                is_highlighted: concept.id === conceptId ? newHighlightedState : false
              }))
            };
          }
          return topic;
        });

        // Update in baskets
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => ({
          ...basket,
          topics: basket.topics.map(topic => {
            if (topic.id === topicId) {
              return {
                ...topic,
                concepts: topic.concepts.map(concept => ({
                  ...concept,
                  is_highlighted: concept.id === conceptId ? newHighlightedState : false
                }))
              };
            }
            return topic;
          })
        }));
      })
      .addCase(updateConceptImportance.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      });
  },
});

// Export actions from the slice
const { 
  addBasket, 
  updateLastFetchedAt, 
  setBasketContentChanged, 
  setCurrentBasket, 
  setBaskets, 
  updateBasketInState, 
  setWorkspaceLoaded, 
  resetWorkspaceState, 
  addBasketToWorkspace, 
  moveBasketToTop,
  applyTopicRecall,
  topicConceptsSynced,
  connectionsReturnSaved,
  connectionsReturnCleared,
} = workspacesSlice.actions;

// Export the reducer as default
export default workspacesSlice.reducer;

// Export all necessary functions and actions
export {
  fetchWorkspaceDetails,
  stampWorkspaceLastOpened,
  deleteTopic,
  deleteBasket,
  addTopic,
  changeTopic,
  updateLastFetchedAt,
  removeTopicFromBasket,
  setBasketContentChanged,
  fetchBasketById,
  setCurrentBasket,
  renameBasket,
  updateBasket,
  setBaskets,
  updateBasketInState,
  setWorkspaceLoaded,
  resetWorkspaceState,
  addBasket,
  addBasketToWorkspace,
  updateConceptImportance,
  moveBasketToTop,
  moveTopic,
  applyTopicRecall,
  topicConceptsSynced,
  connectionsReturnSaved,
  connectionsReturnCleared,
};

// Update the selectCurrentWorkspace selector
export const selectCurrentWorkspace = createSelector(
  [(state) => state.workspaces],
  (workspaces) => ({
    ...workspaces.currentWorkspace,
    isWorkspaceLoaded: workspaces.isWorkspaceLoaded
  })
);

// These memoized selectors use createSelector from Redux Toolkit
// They help optimize performance by only recalculating when their inputs change
// selectCurrentWorkspace is a simple selector that returns the current workspace state
// selectTopicsForWorkspace filters topics for a specific workspace
// selectTopicsForBasket filters topics for a specific basket
export const selectTopicsForWorkspace = createSelector(
  [selectCurrentWorkspace, (state, workspaceId) => workspaceId],
  (currentWorkspace, workspaceId) => 
    currentWorkspace.topics.filter(topic => topic.workspace_id === workspaceId)
);

export const selectCurrentBasket = state => state.workspaces.currentBasket;

export const selectTopicsForBasket = createSelector(
  [selectCurrentWorkspace, selectCurrentBasket, (state, basketId) => basketId],
  (currentWorkspace, currentBasket, basketId) => {
    if (!currentWorkspace || !currentWorkspace.baskets) {
      console.log('No workspace or baskets available');
      return [];
    }

    const basket = basketId ? 
      currentWorkspace.baskets.find(b => b.id === basketId) : 
      currentBasket;

    if (!basket) {
      console.log('Basket not found:', basketId);
      return [];
    }

    console.log('Topics for basket:', basket.topics);
    return sortTopicsByDiscoveryOrder(basket.topics);
  }
);

export const selectWorkspaceDetails = (state, workspaceId) => 
  state.workspaces.workspaces[workspaceId];

// Add these memoized selectors at the bottom of workspacesSlice.js
// Base selectors
const selectCurrentWorkspaceState = state => state.workspaces.currentWorkspace;

// Memoized selectors
export const selectAllTopics = createSelector(
  [selectCurrentWorkspaceState],
  currentWorkspace => currentWorkspace.topics || []
);

/** Topics with no basket (tagged / unfiled — chk_topic_basket_or_tag). */
export const selectUnfiledTopics = createSelector(
  [selectAllTopics],
  (topics) => topics.filter((t) => !t.basket_id)
);

/** Daily cap for Due for Review glance strip + drill-in (Phase 11). */
export const DUE_DAILY_CAP = 15;

export const isRecalledToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return (
    d.getFullYear() === n.getFullYear() &&
    d.getMonth() === n.getMonth() &&
    d.getDate() === n.getDate()
  );
};

/**
 * Basketed, not-yet-ripe topics with prior recall activity — priority queue for Due today,
 * and not successfully recalled today (local day).
 * Sorted lowest recall_count first, then oldest updated_at.
 */
export const selectDueTopics = createSelector(
  [selectAllTopics],
  (topics) =>
    topics
      .filter(
        (t) =>
          !!t.basket_id &&
          !t.is_ripe &&
          (t.total_recalls > 0 || t.not_recalled_count > 0) &&
          !isRecalledToday(t.last_recalled_at)
      )
      .sort((a, b) => {
        const recallDiff = (a.recall_count ?? 0) - (b.recall_count ?? 0);
        if (recallDiff !== 0) return recallDiff;
        const timeA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const timeB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return timeA - timeB;
      })
);

export const selectAllBaskets = createSelector(
  [selectCurrentWorkspaceState],
  currentWorkspace => currentWorkspace.baskets || []
);

export const selectAllTags = createSelector(
  [selectCurrentWorkspaceState],
  currentWorkspace => currentWorkspace.tags || []
);

// Selector for available topics (not in current basket)
export const selectAvailableTopics = createSelector(
  [selectAllTopics, (state, currentBasketId) => currentBasketId],
  (topics, currentBasketId) => 
    topics.filter(topic => topic.basket_id !== currentBasketId)
);

// Selector for topics in a specific basket
export const selectBasketTopics = createSelector(
  [selectAllTopics, (state, basketId) => basketId],
  (topics, basketId) =>
    sortTopicsByDiscoveryOrder(topics.filter(topic => topic.basket_id === basketId))
);

